import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { createMaintenanceReminder } from "@/lib/reminders.functions";

interface Props {
  vehicleLabel: string;
  makeSlug?: string;
  modelSlug?: string;
  odometer: number;
  annualMiles: number;
  nextService?: { service: string; dueAt: number } | undefined;
}

const miles = (n: number) => `${n.toLocaleString()} mi`;

export function MaintenanceReminderForm({
  vehicleLabel,
  makeSlug,
  modelSlug,
  odometer,
  annualMiles,
  nextService,
}: Props) {
  const submit = useServerFn(createMaintenanceReminder);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "done" | "error">("idle");
  const [message, setMessage] = useState("");

  if (!makeSlug || !modelSlug) return null;

  const ready = odometer > 0 && !!nextService;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready || !nextService) return;
    setState("saving");
    try {
      const res = await submit({
        data: {
          email: email.trim(),
          makeSlug: makeSlug!,
          modelSlug: modelSlug!,
          vehicleLabel,
          odometer,
          annualMiles,
          nextService: nextService.service,
          nextDueMiles: nextService.dueAt,
        },
      });
      setState("done");
      setMessage(
        `Reminder set. We'll email you around ${new Date(
          `${res.notifyOn}T12:00:00Z`,
        ).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}, before your ${res.service.toLowerCase()} is due at ${miles(res.dueMiles)}.`,
      );
    } catch {
      setState("error");
      setMessage("Something went wrong saving your reminder. Please try again.");
    }
  }

  return (
    <div className="border-t border-border p-4 sm:p-5">
      <h3 className="font-semibold text-foreground mb-1">
        Get an email before your next service
      </h3>
      <p className="text-sm text-muted-foreground mb-3">
        {ready && nextService ? (
          <>
            Next up: <span className="font-medium text-foreground">{nextService.service}</span>{" "}
            at {miles(nextService.dueAt)}. We'll email you about two weeks before you get
            there, based on {miles(annualMiles)} a year. One email per milestone — no
            newsletters.
          </>
        ) : (
          <>Enter your current odometer reading above to set a reminder.</>
        )}
      </p>
      <form onSubmit={onSubmit} className="flex flex-wrap gap-3">
        <input
          type="email"
          required
          maxLength={254}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          aria-label="Email address for maintenance reminder"
          disabled={!ready || state === "saving"}
          className="w-full sm:w-72 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={!ready || state === "saving"}
          className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
        >
          {state === "saving" ? "Setting reminder…" : "Set reminder"}
        </button>
      </form>
      {message && (
        <p
          role="status"
          className={`mt-3 text-sm ${state === "error" ? "text-destructive" : "text-primary"}`}
        >
          {message}
        </p>
      )}
    </div>
  );
}